<template>
    <div class="documents-tab">
        <table-toolbar
            :show-title="true"
            title="Documents"
            :show-count="true"
            :current-count="filteredDocuments.length"
            :total-count="documentData && documentData.length ? documentData.length : totalDocuments"
            :searchable="true"
            :search-query="searchQuery"
            @search-change="onSearchChange"
            :show-view-toggle="showViewToggle"
            :view-mode="viewMode"
            @view-mode-change="viewMode = $event"
            :show-sort-filter="true"
            :filter-options="filterOptions"
            @filter-change="handleFilterChange"
            @clear-filters="clearFilters"
            :show-refresh="false"
            :per-page="perPage"
            :show-per-page-selector="false"
        >
            <template #action-buttons>
                <themed-button 
                    v-if="$gate.can('create', 'document')"
                    @click="showDocumentUpload"
                    variant="default"
                >
                    <theme-icon icon="upload" type="icons" classes="flex w-4 h-4" />
                    <span>Upload</span>
                </themed-button>
            </template>
        </table-toolbar>



        <!-- Grid View -->
        <themed-grid
            v-if="viewMode === 'grid'"
            :data="filteredDocuments"
            :fields="[]"
            :columns="4"
            :per-page="perPage"
            :current-page="currentPage"
            @update:currentPage="currentPage = $event"
            empty-message="No documents found"
            empty-icon="file"
            :clickable="false"
            custom-class="documents-grid"
        >
            <template #default="{ item: document }">
                <!-- Thumbnail -->
                <div class="documents-tab__card-thumbnail">
                    <div v-if="document.is_image">
                        <a :href="`#${document.name}`">
                            <img 
                                :src="document.view_url" 
                                :alt="document.name"
                                class="documents-tab__card-image"
                            />
                        </a>
                        <a href="#_" class="lightbox" :id="`${document.name}`">
                            <div class="relative">
                                <theme-icon 
                                    icon="close" 
                                    type="icons" 
                                    class="absolute top-0 right-0 text-white hover:text-gray-300 h-8 w-8 cursor-pointer"
                                />
                                <img :src="document.view_url" :alt="document.name" />
                            </div>
                        </a>
                    </div>
                    <div v-else-if="document.is_video">
                        <a :href="`#${document.name}`">
                            <div v-html="document.icon" class="documents-tab__card-icon"></div>
                        </a>
                        <a href="#_" class="lightbox" :id="`${document.name}`">
                            <video width="100%" height="100%" controls>
                                <source :src="document.view_url" :type="'video/' + document.type">
                                Your browser does not support the video tag.
                            </video>
                        </a>
                    </div>
                    <div v-else v-html="document.icon" class="documents-tab__card-icon"></div>
                </div>

                <!-- Card Content -->
                <div class="documents-tab__card-content">
                    <div class="documents-tab__card-header">
                        <div v-html="document.icon" class="documents-tab__card-icon-small"></div>
                        <div class="documents-tab__card-category">
                            <span class="documents-tab__card-category-name">{{ document.category_name || '-' }}</span>
                            <span v-if="document.subcategory_name" class="documents-tab__card-subcategory-name">
                                {{ document.subcategory_name }}
                            </span>
                        </div>
                    </div>

                    <div class="documents-tab__card-info">
                        <p class="documents-tab__card-title" :title="document.name">
                            {{ shortenFileName(document.name, 20) }}
                        </p>
                        <p v-if="document.description" class="documents-tab__card-description">
                            {{ document.description }}
                        </p>
                    </div>
                </div>

                <!-- Card Footer -->
                <div class="documents-tab__card-footer">
                    <div class="documents-tab__card-meta">
                        <span class="documents-tab__card-uploader">{{ document.uploaded_by_name || '-' }}</span>
                        <span class="documents-tab__card-date">{{ document.uploaded_at }}</span>
                    </div>
                    <div class="documents-tab__card-actions">
                        <themed-button
                            v-if="$gate.can('update', 'document', document)"
                            @click="editDocument(document)"
                            variant="ghost"
                            size="sm"
                            title="Edit Document"
                        >
                            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
                        </themed-button>
                        <themed-button
                            @click="downloadDocument(document)"
                            variant="ghost"
                            size="sm"
                            title="Download Document"
                        >
                            <theme-icon icon="download" type="icons" classes="w-4 h-4" />
                        </themed-button>
                        <themed-button
                            v-if="$gate.can('delete', 'document', document)"
                            @click="confirmDeleteDocument(document)"
                            variant="ghost"
                            size="sm"
                            title="Delete Document"
                        >
                            <theme-icon icon="trash-icon" type="icons" classes="w-4 h-4" />
                        </themed-button>
                    </div>
                </div>
            </template>
        </themed-grid>

        <!-- Table View -->
        <themed-table
            v-else
            :columns="columns"
            :data="filteredDocuments"
            :loading="loading"
            :pagination="documentData && documentData.length ? false : { current: currentPage, total: lastPage, perPage: perPage, totalItems: totalDocuments }"
            :searchable="false"
            :sortable="true"
            :striped="true"
            :hoverable="true"
            @page-change="onPageChange"
            @action="handleAction"
        >
            <template #column-name="{ row }">
                <div class="documents-tab__document-cell">
                    <!-- Image thumbnail with lightbox -->
                    <div v-if="row.is_image" class="documents-tab__thumbnail-wrapper">
                        <a :href="`#${row.name}`">
                            <img 
                                :src="row.view_url" 
                                :alt="row.name"
                                class="documents-tab__thumbnail"
                            />
                        </a>
                        <a href="#_" class="lightbox" :id="`${row.name}`">
                            <div class="relative">
                                <theme-icon 
                                    icon="close" 
                                    type="icons" 
                                    class="absolute top-0 right-0 text-white hover:text-gray-300 h-8 w-8 cursor-pointer"
                                />
                                <img :src="row.view_url" :alt="row.name" />
                            </div>
                        </a>
                    </div>
                    
                    <!-- Video thumbnail with lightbox -->
                    <div v-else-if="row.is_video" class="documents-tab__thumbnail-wrapper">
                        <a :href="`#${row.name}`">
                            <div class="documents-tab__file-icon-container">
                                <div v-html="row.icon" class="documents-tab__file-icon"></div>
                                <span class="documents-tab__file-type">{{ row.type.toUpperCase() }}</span>
                            </div>
                        </a>
                        <a href="#_" class="lightbox" :id="`${row.name}`">
                            <video width="100%" height="100%" controls>
                                <source :src="row.view_url" :type="'video/' + row.type">
                                Your browser does not support the video tag.
                            </video>
                        </a>
                    </div>
                    
                    <!-- File icon for other types (PDF, etc) -->
                    <div v-else class="documents-tab__thumbnail-wrapper">
                        <div class="documents-tab__file-icon-container">
                            <div v-html="row.icon" class="documents-tab__file-icon"></div>
                            <span class="documents-tab__file-type">{{ row.type.toUpperCase() }}</span>
                        </div>
                    </div>
                    
                    <!-- Document name and description -->
                    <div class="documents-tab__document-info">
                        <a 
                            v-if="row.is_image || row.is_video"
                            :href="`#${row.name}`"
                            class="documents-tab__document-link"
                            :title="row.name"
                        >
                            {{ shortenFileName(row.name, 30) }}
                        </a>
                        <a 
                            v-else
                            :href="row.view_url" 
                            target="_blank"
                            class="documents-tab__document-link"
                            :title="row.name"
                        >
                            {{ shortenFileName(row.name, 30) }}
                        </a>
                        <p v-if="row.description" class="documents-tab__document-description">
                            {{ row.description }}
                        </p>
                    </div>
                </div>
            </template>
        </themed-table>

        <!-- Upload Document Modal -->
        <upload-document
            :documentable-id="documentableId"
            :documentable-type="documentableType"
            @documents-uploaded="handleDocumentsUploaded"
        />

        <!-- Edit Document Modal -->
        <edit-document
            v-if="selectedDocument"
            :document-data="selectedDocument"
            @document-updated="handleDocumentUpdated"
        />

        <notifications classes="notification" position="bottom right" />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TableToolbar from '../table/TableToolbar.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemeIcon from '../ThemeIcon.vue';
import UploadDocument from './UploadDocumentNative.vue';
import EditDocument from './EditDocument.vue';

export default {
    name: 'DocumentsV1',
    
    components: {
        TableToolbar,
        ThemedTable,
        ThemedButton,
        ThemedGrid,
        ThemeIcon,
        UploadDocument,
        EditDocument
    },

    props: {
        documentData: {
            type: Array,
            default: () => []
        },
        documentableId: {
            type: Number,
            required: true
        },
        documentableType: {
            type: String,
            required: true
        },
        showCategoryMenu: {
            type: Boolean,
            default: false
        },
        showViewToggle: {
            type: Boolean,
            default: false
        },
        defaultViewMode: {
            type: String,
            default: 'list',
            validator: (v) => ['grid', 'list'].includes(v)
        },
        initialCategory: {
            type: Number,
            required: false
        },
        initialSubcategory: {
            type: Number,
            required: false
        }
    },

    data() {
        return {
            loading: false,
            allDocuments: [],
            searchQuery: '',
            viewMode: this.defaultViewMode,
            selectedCategory: this.initialCategory || '',
            selectedSubCategory: this.initialSubcategory || '',
            selectedDocument: null,
            currentPage: 1,
            perPage: 25,
            totalDocuments: 0,
            lastPage: 1,
            columns: [
                {
                    field: 'name',
                    label: 'DOCUMENT',
                    sortable: true,
                    width: '35%',
                    formatter: (value, row) => {
                        // For row details popup, just show the name
                        return value || '-';
                    }
                },
                {
                    field: 'category_name',
                    label: 'CATEGORY',
                    sortable: true,
                    width: '20%',
                    formatter: (value, row) => {
                        let result = row.category_name || '-';
                        if (row.subcategory_name) {
                            result += ` / ${row.subcategory_name}`;
                        }
                        return result;
                    }
                },
                {
                    field: 'uploaded_by_name',
                    label: 'UPLOADED BY',
                    sortable: true,
                    formatter: (value, row) => {
                        let result = row.uploaded_by_name || '-';
                        if (row.uploaded_at) {
                            result += ` ${row.uploaded_at}`;
                        }
                        return result;
                    }
                },
                {
                    field: 'type',
                    label: 'TYPE',
                    sortable: true,
                    width: '80px',
                    align:'center',
                    formatter: (value) => value ? value.toUpperCase() : '-'
                },
                {
                    field: 'size',
                    label: 'SIZE',
                    sortable: true,
                    width: '80px',
                    align:'right',
                    formatter: (value, row) => row.readable_size || '-'
                },
                {
                    field: 'actions',
                    label: 'Actions',
                    type: 'actions',
                    align:'center',
                    sortable: false,
                    width: '10%',
                    buttons: [
                        {
                            name: 'edit',
                            icon: 'edit',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: (row) => this.$gate.can('update', 'document', row)
                        },
                        {
                            name: 'download',
                            icon: 'download',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true
                        },
                        {
                            name: 'delete',
                            icon: 'trash',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: (row) => this.$gate.can('delete', 'document', row)
                        }
                    ]
                }
            ]
        };
    },

    computed: {
        ...mapState(['documents']),

        filteredDocuments() {
            // When used as embedded tab (documentData prop provided), filter client-side
            if (this.documentData && this.documentData.length) {
                let docs = this.documentData;
                if (this.selectedCategory) docs = docs.filter(d => d.category_id === this.selectedCategory);
                if (this.selectedSubCategory) docs = docs.filter(d => d.subcategory_id === this.selectedSubCategory);
                if (this.searchQuery) {
                    const q = this.searchQuery.toLowerCase();
                    docs = docs.filter(d =>
                        d.name?.toLowerCase().includes(q) ||
                        d.description?.toLowerCase().includes(q) ||
                        d.category_name?.toLowerCase().includes(q) ||
                        d.uploaded_by_name?.toLowerCase().includes(q)
                    );
                }
                return docs;
            }
            // Standalone page — data already filtered server-side
            return this.allDocuments;
        },

        availableCategories() {
            return this.documents.categories || [];
        },

        availableSubcategories() {
            if (!this.selectedCategory) {
                return [];
            }
            // Convert to numbers for comparison
            const categoryId = parseInt(this.selectedCategory);
            return (this.documents.subcategories || []).filter(
                sub => parseInt(sub.category_id) === categoryId
            );
        },

        categoryOptions() {
            return this.availableCategories.map(cat => ({
                value: cat.id,
                label: cat.name
            }));
        },

        subcategoryOptions() {
            return this.availableSubcategories.map(sub => ({
                value: sub.id,
                label: sub.name
            }));
        },

        filterOptions() {
            const filters = [];

            // Category select filter
            filters.push({
                type: 'select',
                label: 'Category',
                model: 'category',
                key: 'filter-category',
                value: this.selectedCategory,
                options: [
                    { label: 'All Categories', value: '' },
                    ...this.categoryOptions
                ],
                placeholder: 'All Categories'
            });

            // Subcategory select filter (only show if category is selected)
            if (this.selectedCategory) {
                filters.push({
                    type: 'select',
                    label: 'Subcategory',
                    model: 'subcategory',
                    key: 'filter-subcategory',
                    value: this.selectedSubCategory,
                    options: [
                        { label: 'All Subcategories', value: '' },
                        ...this.subcategoryOptions
                    ],
                    placeholder: 'All Subcategories'
                });
            }

            return filters;
        }
    },

    mounted() {
        this.$store.dispatch('loadCategories');
        
        // Load saved view mode preference
        const savedViewMode = localStorage.getItem('documents_view_mode');
        if (savedViewMode && ['grid', 'list'].includes(savedViewMode)) {
            this.viewMode = savedViewMode;
        }

        // If no prop data, fetch from API (standalone page mode)
        if (!this.documentData || !this.documentData.length) {
            this.fetchDocuments();
        }

        this.$eventHub.$on('documents-menu-filter-changed', this.handleMenuFilterChanged);
    },

    beforeDestroy() {
        // Clean up event listener
        this.$eventHub.$off('documents-menu-filter-changed', this.handleMenuFilterChanged);
    },

    watch: {
        viewMode(newValue) {
            localStorage.setItem('documents_view_mode', newValue);
        },

        filteredDocuments() {
            this.currentPage = 1;
        },

        selectedCategory(newCategory) {
            if (!newCategory) {
                this.selectedSubCategory = '';
            }
            if (!this.documentData || !this.documentData.length) {
                this.currentPage = 1;
                this.fetchDocuments();
            }
        },

        selectedSubCategory() {
            if (!this.documentData || !this.documentData.length) {
                this.currentPage = 1;
                this.fetchDocuments();
            }
        }
    },

    methods: {
        fetchDocuments() {
            if (this.documentData && this.documentData.length) return;
            this.loading = true;
            const params = {
                page: this.currentPage,
                per_page: this.perPage,
            };
            if (this.selectedCategory) params.category_id = this.selectedCategory;
            if (this.selectedSubCategory) params.subcategory_id = this.selectedSubCategory;
            if (this.searchQuery) params.search = this.searchQuery;

            axios.get('/api/documents', { params })
                .then(({ data }) => {
                    this.allDocuments = data.data;
                    this.totalDocuments = data.total;
                    this.lastPage = data.last_page;
                })
                .catch(err => console.error('Failed to load documents', err))
                .finally(() => { this.loading = false; });
        },

        onPageChange({ page }) {
            this.currentPage = page;
            this.fetchDocuments();
        },

        onSearchChange(query) {
            this.searchQuery = query;
            if (this.documentData && this.documentData.length) return;
            clearTimeout(this._searchTimer);
            this._searchTimer = setTimeout(() => {
                this.currentPage = 1;
                this.fetchDocuments();
            }, 350);
        },

        handleAction({ action, row }) {
            switch (action) {
                case 'edit':
                    this.editDocument(row);
                    break;
                case 'download':
                    this.downloadDocument(row);
                    break;
                case 'delete':
                    this.confirmDeleteDocument(row);
                    break;
            }
        },

        showDocumentUpload() {
            this.$modal.show('document-upload');
        },

        editDocument(document) {
            // Set the document object directly
            this.selectedDocument = document;
            
            // Open modal immediately
            this.$nextTick(() => {
                this.$modal.show('document-edit');
            });
        },

        confirmDeleteDocument(document) {
            this.$swal({
                title: 'Are you sure?',
                text: "Are you sure you want to delete this document?",
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteDocument(document);
                }
            });
        },

        deleteDocument(document) {
            axios.delete('/documents/' + document.id)
                .then(response => {
                    // Remove from local data
                    this.removeDocument(document.id);
                    
                    // Emit event for gallery
                    this.$eventHub.$emit('remove-from-gallery', document.view_url);
                    
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Document deleted successfully'
                    });
                })
                .catch(error => {
                    console.error(error);
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to delete document'
                    });
                });
        },

        removeDocument(documentId) {
            this.allDocuments = this.allDocuments.filter(doc => doc.id !== documentId);
            this.totalDocuments = Math.max(0, this.totalDocuments - 1);
            this.$emit('document-deleted', documentId);
        },

        handleDocumentsUploaded(documents) {
            // Refresh from API to get correct pagination
            this.fetchDocuments();
            this.$eventHub.$emit('add-to-gallery', documents);
            this.$emit('documents-uploaded', documents);
            this.$notify({ type: 'success', title: 'Success', text: 'Documents uploaded successfully' });
        },

        getInitials(name) {
            if (!name) return '?';
            const parts = name.split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },

        handleFilterChange(event) {
            if (event.filter === 'category') {
                this.selectedCategory = event.value;
                // Reset subcategory when category changes
                if (!event.value) {
                    this.selectedSubCategory = '';
                }
            } else if (event.filter === 'subcategory') {
                this.selectedSubCategory = event.value;
            }
        },

        clearFilters() {
            this.selectedCategory = '';
            this.selectedSubCategory = '';
        },

        handleMenuFilterChanged(filters) {
            this.selectedCategory = filters.category || '';
            this.selectedSubCategory = filters.subcategory || '';
        },

        handleFiltersModalClosed() {
            // Modal closed, filters are already applied
        },

        handleDocumentUpdated(document) {
            const index = this.allDocuments.findIndex(doc => doc.id === document.id);
            if (index !== -1) {
                this.$set(this.allDocuments, index, document);
            }
            this.$emit('document-updated', document);
        },

        downloadDocument(document) {
            window.location = document.download_url;
        },

        getFileIcon(type) {
            const iconMap = {
                pdf: 'file-pdf',
                doc: 'file-doc',
                docx: 'file-doc',
                xls: 'file-xls',
                xlsx: 'file-xls',
                jpg: 'file-image',
                jpeg: 'file-image',
                png: 'file-image',
                gif: 'file-image',
                svg: 'file-image',
                txt: 'file-text',
                csv: 'file-xls'
            };
            
            return iconMap[type?.toLowerCase()] || 'file';
        },

        shortenFileName(name, maxLength = 30) {
            if (!name || name.length <= maxLength) {
                return name;
            }
            
            const extension = name.split('.').pop();
            const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
            const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
            
            return `${truncatedName}...${extension}`;
        }
    }
};
</script>
